import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable, fromEvent, merge } from 'rxjs';
import { map, distinctUntilChanged } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';

export type OperationType = 'create' | 'update' | 'delete';
export type EntityType = 
  | 'contract' 
  | 'vessel' 
  | 'marina' 
  | 'marina-visit' 
  | 'marina-service' 
  | 'legal-entity' 
  | 'price-season' 
  | 'price-list' 
  | 'price-rule';

export interface PendingOperation {
  id: string;  // UUID for the operation itself
  entityType: EntityType;
  entityId: string;  // UUID of the entity
  operation: OperationType;
  data: any;
  timestamp: number;
  retryCount: number;
  lastError?: string;
  validationErrors?: Array<{ field: string; message: string }>;
}

export interface SyncResult {
  success: boolean;
  operation: PendingOperation;
  error?: string;
  validationErrors?: Array<{ field: string; message: string }>;
}

const PENDING_OPERATIONS_KEY = 'mm_pending_operations';
const OFFLINE_ENTITIES_KEY = 'mm_offline_entities';

@Injectable({
  providedIn: 'root'
})
export class OfflineService {
  private isOnlineSubject = new BehaviorSubject<boolean>(navigator.onLine);
  private pendingOperationsSubject = new BehaviorSubject<PendingOperation[]>([]);
  private syncingSubject = new BehaviorSubject<boolean>(false);
  private failedSyncSubject = new BehaviorSubject<PendingOperation[]>([]);
  
  isOnline$ = this.isOnlineSubject.asObservable();
  pendingOperations$ = this.pendingOperationsSubject.asObservable();
  syncing$ = this.syncingSubject.asObservable();
  failedSync$ = this.failedSyncSubject.asObservable();

  private apiEndpoints: Record<EntityType, string> = {
    'contract': '/api/v1/contracts',
    'vessel': '/api/v1/vessels',
    'marina': '/api/v1/marinas',
    'marina-visit': '/api/v1/marina-visits',
    'marina-service': '/api/v1/services',
    'legal-entity': '/api/v1/legal-entities',
    'price-season': '/api/v1/price-seasons',
    'price-list': '/api/v1/price-lists',
    'price-rule': '/api/v1/price-rules'
  };

  private entityRoutes: Record<EntityType, string> = {
    'contract': '/contracts',
    'vessel': '/vessels',
    'marina': '/marinas',
    'marina-visit': '/marina-visits',
    'marina-service': '/marina-services',
    'legal-entity': '/legal-entities',
    'price-season': '/marina-prices/seasons',
    'price-list': '/marina-prices/price-lists',
    'price-rule': '/marina-prices/price-rules'
  };

  constructor(
    private http: HttpClient,
    private router: Router,
    private messageService: MessageService,
    private ngZone: NgZone
  ) {
    this.initOnlineStatusListener();
    this.loadPendingOperations();
    
    console.log('[OfflineService] Initialized, navigator.onLine:', navigator.onLine);
    
    // Verify actual connectivity on startup
    this.verifyConnectivity();
  }

  private initOnlineStatusListener(): void {
    // Listen for online/offline events
    this.ngZone.runOutsideAngular(() => {
      window.addEventListener('online', () => {
        this.ngZone.run(() => {
          console.log('[OfflineService] 🌐 Browser reports online!');
          // Verify actual connectivity before trusting it
          this.verifyConnectivity();
        });
      });

      window.addEventListener('offline', () => {
        this.ngZone.run(() => {
          console.log('[OfflineService] 📴 Browser reports offline!');
          this.isOnlineSubject.next(false);
          this.messageService.add({
            severity: 'warn',
            summary: 'Offline Mode',
            detail: 'You are now offline. Changes will be saved locally.',
            life: 5000
          });
        });
      });
    });
  }

  /**
   * Verify actual connectivity by pinging a lightweight endpoint
   * This is more reliable than navigator.onLine
   */
  private async verifyConnectivity(): Promise<void> {
    try {
      // Try to fetch a lightweight endpoint (HEAD request to current origin)
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout
      
      const response = await fetch(window.location.origin + '/api/v1/health', {
        method: 'HEAD',
        signal: controller.signal,
        cache: 'no-cache'
      }).catch(() => null);
      
      clearTimeout(timeoutId);
      
      const isOnline = response !== null && response.ok;
      
      if (isOnline !== this.isOnlineSubject.value) {
        console.log('[OfflineService] Connectivity verified, actual status:', isOnline ? 'ONLINE' : 'OFFLINE');
        this.isOnlineSubject.next(isOnline);
        
        if (isOnline) {
          this.messageService.add({
            severity: 'success',
            summary: 'Back Online',
            detail: 'Connection restored. Syncing pending changes...',
            life: 3000
          });
          // Automatically sync when back online
          this.syncPendingOperations();
        }
      }
    } catch (error) {
      console.error('[OfflineService] Connectivity check failed:', error);
      this.isOnlineSubject.next(false);
    }
  }

  get isOnline(): boolean {
    return this.isOnlineSubject.value;
  }

  get pendingOperationsCount(): number {
    return this.pendingOperationsSubject.value.length;
  }

  /**
   * Queue an operation for later sync (when offline) or execute immediately (when online)
   */
  async queueOperation(
    entityType: EntityType,
    entityId: string,
    operation: OperationType,
    data: any
  ): Promise<{ queued: boolean; result?: any; error?: any }> {
    const pendingOp: PendingOperation = {
      id: this.generateUUID(),
      entityType,
      entityId,
      operation,
      data: { ...data },
      timestamp: Date.now(),
      retryCount: 0
    };

    if (this.isOnline) {
      // Try to execute immediately
      try {
        const result = await this.executeOperation(pendingOp);
        return { queued: false, result };
      } catch (error: any) {
        // If it's a network error, queue it
        if (this.isNetworkError(error)) {
          this.addToPendingOperations(pendingOp);
          this.isOnlineSubject.next(false);
          return { queued: true };
        }
        // Otherwise, throw the error (validation errors, etc.)
        throw error;
      }
    } else {
      // Offline - queue the operation
      this.addToPendingOperations(pendingOp);
      
      // Also store the entity data locally for potential display
      this.storeOfflineEntity(entityType, entityId, data);
      
      return { queued: true };
    }
  }

  /**
   * Execute a single operation against the server
   */
  private async executeOperation(op: PendingOperation): Promise<any> {
    const endpoint = this.apiEndpoints[op.entityType];
    const headers = this.getAuthHeaders();

    switch (op.operation) {
      case 'create':
        return this.http.post(`${endpoint}`, op.data, { headers }).toPromise();
      
      case 'update':
        return this.http.put(`${endpoint}/${op.entityId}`, op.data, { headers }).toPromise();
      
      case 'delete':
        return this.http.delete(`${endpoint}/${op.entityId}`, { headers }).toPromise();
      
      default:
        throw new Error(`Unknown operation type: ${op.operation}`);
    }
  }

  /**
   * Sync all pending operations when back online
   */
  async syncPendingOperations(): Promise<void> {
    if (!this.isOnline || this.syncingSubject.value) {
      return;
    }

    const pending = this.pendingOperationsSubject.value;
    if (pending.length === 0) {
      return;
    }

    console.log('[OfflineService] Starting sync of', pending.length, 'pending operations');
    this.syncingSubject.next(true);

    const results: SyncResult[] = [];
    const stillPending: PendingOperation[] = [];
    const failed: PendingOperation[] = [];

    // Process operations in order (by timestamp)
    const sortedOps = [...pending].sort((a, b) => a.timestamp - b.timestamp);

    for (const op of sortedOps) {
      try {
        console.log(`[OfflineService] Syncing ${op.operation} ${op.entityType} ${op.entityId}`);
        await this.executeOperation(op);
        
        results.push({ success: true, operation: op });
        
        // Remove from offline entities storage
        this.removeOfflineEntity(op.entityType, op.entityId);
        
      } catch (error: any) {
        console.error(`[OfflineService] Sync failed for ${op.entityType} ${op.entityId}:`, error);
        
        if (this.isNetworkError(error)) {
          // Network error - keep in queue for retry
          stillPending.push({ ...op, retryCount: op.retryCount + 1 });
        } else if (error.status === 400 && error.error?.validationErrors) {
          // Validation error - mark as failed and store errors
          const failedOp: PendingOperation = {
            ...op,
            retryCount: op.retryCount + 1,
            lastError: 'Validation failed',
            validationErrors: error.error.validationErrors
          };
          failed.push(failedOp);
          results.push({
            success: false,
            operation: failedOp,
            validationErrors: error.error.validationErrors
          });
        } else {
          // Other error - keep in queue with error info
          stillPending.push({
            ...op,
            retryCount: op.retryCount + 1,
            lastError: error.message || 'Unknown error'
          });
        }
      }
    }

    // Update pending operations
    this.pendingOperationsSubject.next(stillPending);
    this.savePendingOperations();

    // Update failed syncs (validation errors that need user attention)
    this.failedSyncSubject.next(failed);

    this.syncingSubject.next(false);

    // Show summary message
    const successCount = results.filter(r => r.success).length;
    const failedCount = results.filter(r => !r.success).length;

    if (successCount > 0) {
      this.messageService.add({
        severity: 'success',
        summary: 'Sync Complete',
        detail: `${successCount} operation(s) synced successfully.`,
        life: 3000
      });
    }

    if (failedCount > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Sync Issues',
        detail: `${failedCount} operation(s) require attention. Check pending changes.`,
        life: 5000
      });
    }

    console.log('[OfflineService] Sync complete. Success:', successCount, 'Failed:', failedCount, 'Still pending:', stillPending.length);
  }

  /**
   * Open the edit form for a failed operation so user can fix validation errors
   */
  openFailedOperation(op: PendingOperation): void {
    const route = this.entityRoutes[op.entityType];
    
    // Store the failed operation data and errors for the edit form to pick up
    sessionStorage.setItem('mm_failed_operation', JSON.stringify(op));
    
    // Navigate to the edit form
    if (op.operation === 'create') {
      this.router.navigate([route, 'new']);
    } else {
      this.router.navigate([route, op.entityId, 'edit']);
    }
  }

  /**
   * Get failed operation data (called by edit forms on init)
   */
  getFailedOperationData(): PendingOperation | null {
    const data = sessionStorage.getItem('mm_failed_operation');
    if (data) {
      sessionStorage.removeItem('mm_failed_operation');
      return JSON.parse(data);
    }
    return null;
  }

  /**
   * Remove a pending operation (after successful sync or user cancellation)
   */
  removePendingOperation(operationId: string): void {
    const pending = this.pendingOperationsSubject.value.filter(op => op.id !== operationId);
    this.pendingOperationsSubject.next(pending);
    this.savePendingOperations();
  }

  /**
   * Clear all failed sync operations
   */
  clearFailedOperations(): void {
    const failed = this.failedSyncSubject.value;
    failed.forEach(op => {
      this.removePendingOperation(op.id);
      this.removeOfflineEntity(op.entityType, op.entityId);
    });
    this.failedSyncSubject.next([]);
  }

  /**
   * Get pending operations for a specific entity
   */
  getPendingOperationsForEntity(entityType: EntityType, entityId: string): PendingOperation[] {
    return this.pendingOperationsSubject.value.filter(
      op => op.entityType === entityType && op.entityId === entityId
    );
  }

  /**
   * Check if an entity has pending changes
   */
  hasPendingChanges(entityType: EntityType, entityId: string): boolean {
    return this.getPendingOperationsForEntity(entityType, entityId).length > 0;
  }

  // ============ Local Storage Operations ============

  private addToPendingOperations(op: PendingOperation): void {
    const pending = [...this.pendingOperationsSubject.value];
    
    // Check if there's already a pending operation for this entity
    const existingIndex = pending.findIndex(
      p => p.entityType === op.entityType && p.entityId === op.entityId
    );

    if (existingIndex >= 0) {
      // Replace existing operation (latest change wins)
      if (op.operation === 'delete') {
        // If deleting, remove create operation or replace update with delete
        if (pending[existingIndex].operation === 'create') {
          // Created offline then deleted - just remove both
          pending.splice(existingIndex, 1);
        } else {
          pending[existingIndex] = op;
        }
      } else {
        // Update the existing operation with new data
        pending[existingIndex] = {
          ...pending[existingIndex],
          data: op.data,
          timestamp: op.timestamp
        };
      }
    } else {
      pending.push(op);
    }

    this.pendingOperationsSubject.next(pending);
    this.savePendingOperations();
  }

  private loadPendingOperations(): void {
    try {
      const stored = localStorage.getItem(PENDING_OPERATIONS_KEY);
      if (stored) {
        const operations = JSON.parse(stored) as PendingOperation[];
        this.pendingOperationsSubject.next(operations);
        console.log('[OfflineService] Loaded', operations.length, 'pending operations from storage');
      }
    } catch (error) {
      console.error('[OfflineService] Error loading pending operations:', error);
    }
  }

  private savePendingOperations(): void {
    try {
      const operations = this.pendingOperationsSubject.value;
      localStorage.setItem(PENDING_OPERATIONS_KEY, JSON.stringify(operations));
    } catch (error) {
      console.error('[OfflineService] Error saving pending operations:', error);
    }
  }

  private storeOfflineEntity(entityType: EntityType, entityId: string, data: any): void {
    try {
      const key = `${OFFLINE_ENTITIES_KEY}_${entityType}`;
      const stored = localStorage.getItem(key);
      const entities = stored ? JSON.parse(stored) : {};
      entities[entityId] = { ...data, _offlineCreated: true, _offlineTimestamp: Date.now() };
      localStorage.setItem(key, JSON.stringify(entities));
    } catch (error) {
      console.error('[OfflineService] Error storing offline entity:', error);
    }
  }

  private removeOfflineEntity(entityType: EntityType, entityId: string): void {
    try {
      const key = `${OFFLINE_ENTITIES_KEY}_${entityType}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        const entities = JSON.parse(stored);
        delete entities[entityId];
        localStorage.setItem(key, JSON.stringify(entities));
      }
    } catch (error) {
      console.error('[OfflineService] Error removing offline entity:', error);
    }
  }

  /**
   * Get offline entities of a specific type (for display in lists)
   */
  getOfflineEntities(entityType: EntityType): any[] {
    try {
      const key = `${OFFLINE_ENTITIES_KEY}_${entityType}`;
      const stored = localStorage.getItem(key);
      if (stored) {
        const entities = JSON.parse(stored);
        return Object.values(entities);
      }
    } catch (error) {
      console.error('[OfflineService] Error getting offline entities:', error);
    }
    return [];
  }

  // ============ Helper Methods ============

  private getAuthHeaders(): HttpHeaders {
    const token = localStorage.getItem('auth_token');
    const credentials = localStorage.getItem('auth_credentials');
    
    let headers = new HttpHeaders({
      'Content-Type': 'application/json'
    });

    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    } else if (credentials) {
      headers = headers.set('Authorization', `Basic ${credentials}`);
    }

    return headers;
  }

  private isNetworkError(error: any): boolean {
    return (
      error.status === 0 || // Network error
      error.status === 503 || // Service unavailable
      error.status === 504 || // Gateway timeout
      error.name === 'TimeoutError' ||
      !navigator.onLine
    );
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}

